import {SandpackFile, SandpackFiles} from '@codesandbox/sandpack-react/types';
import {pickBy, values} from 'lodash';
import {create} from 'zustand';
import _ from 'lodash';
import {createSelectors} from '../zustand';
import {v4 as uuidv4} from 'uuid';
import {formatSandpackCode, getStyleCSS, getIndexFile} from '../../Views/AICodeGen/sandpack';
import {GPTMessage} from '../../Common/api/GPTCodeGen';
import {
    AstView,
    ChatHistory,
    ChatMessage,
    DEFAULT,
    PROMPT_TYPE,
    SystemChatMessage,
    SystemChatMessageContent,
    UserChatMessage,
} from 'rendition-core';
import {RECOMMENDATION_PROMPT} from 'Views/AICodeGen/constants';
export const APP_FILE_NAME = '/App.tsx';

export const UNSAVED = 'UNSAVED';
export const SAVED = 'SAVED';
export const SAVING = 'SAVING';

export type SaveStatus = typeof UNSAVED | typeof SAVED | typeof SAVING;

export interface AiGenState {
    setFiles: (files: Record<string, SandpackFile>) => void;
    scopedCode: string;
    setScopedCode: (scopedCode: string) => void;
    hasUserModifiedCode: boolean;
    setHasUserModifiedCode: (hasUserModifiedCode: boolean) => void;
    chatHistory: ChatHistory;
    setChatHistory: (chatHistory: ChatHistory) => void;
    selectedSystemMessage: string;
    selectedUserMessage: string;
    setSelectedSystemMessage: (selectedSystemMessage: string) => void;
    setSelectedUserMessage: (selectedUserMessage: string) => void;
    promptType: PROMPT_TYPE;
    prompt: string;
    setPrompt: (prompt: string) => void;
    setPromptType: (promptType: PROMPT_TYPE) => void;
    error: string;
    setError: (error: string) => void;
    saveStatus: SaveStatus;
    setSaveStatus: (saveStatus: SaveStatus) => void;
    setStreamingSystemMessageId: (id: string) => void;
    chatId: string;
    setChatId: (chatInput: string) => void;
    docId: string;
    previewWidth: number;
    setPreviewWidth: (previewWidth: number) => void;
    setDocId: (docId: string) => void;
    streamingSystemMessageId: string;
    computed: {
        isStreaming: boolean;
        files: Record<string, SandpackFile> | undefined;
    };
    resetState: () => void;
    initialSummary: boolean;
    setInitialSummary: (initialSummary: boolean) => void;
}

export const useAiGenStoreBase = create<AiGenState>()((set, get) => {
    const defaultState: AiGenState = {
        error: '',
        hasUserModifiedCode: false,
        scopedCode: '',
        chatHistory: {},
        selectedSystemMessage: '',
        selectedUserMessage: '',
        promptType: DEFAULT,
        prompt: '',
        saveStatus: UNSAVED,
        streamingSystemMessageId: '',
        chatId: '',
        docId: '',

        previewWidth: 1000,
        setPreviewWidth: (previewWidth) => set(() => ({previewWidth})),

        setError: (error) => set(() => ({error})),
        setFiles: (files) => {
            const state = get();
            const newHistory = {
                ...state.chatHistory,
                [state.selectedSystemMessage]: {...state.chatHistory[state.selectedSystemMessage], files},
            };
            set(() => ({chatHistory: newHistory, hasUserModifiedCode: true}));
        },
        setChatId: (chatId) => set(() => ({chatId})),
        setDocId: (docId) => set(() => ({docId})),
        setHasUserModifiedCode: (hasUserModifiedCode) => set(() => ({hasUserModifiedCode})),
        setScopedCode: (scopedCode) => set(() => ({scopedCode})),
        resetState: () => set(() => defaultState),
        setSaveStatus: (saveStatus) => set(() => ({saveStatus})),
        setPromptType: (promptType) => set(() => ({promptType})),
        setChatHistory: (chatHistory) => set(() => ({chatHistory})),
        setStreamingSystemMessageId: (streamingSystemMessageId) => set(() => ({streamingSystemMessageId})),
        setSelectedUserMessage: (selectedUserMessage) => {
            const message = get().chatHistory[selectedUserMessage] as UserChatMessage;
            set(() => ({selectedUserMessage, prompt: message.content}));
        },
        setSelectedSystemMessage: (selectedSystemMessage) => {
            set(() => ({
                selectedSystemMessage,
                files: (get().chatHistory[selectedSystemMessage] as SystemChatMessage).files,
            }));
        },
        setPrompt: (prompt) => {
            const state = get();
            const newHistory = {
                ...state.chatHistory,
                [state.selectedUserMessage]: {
                    ...state.chatHistory[state.selectedUserMessage],
                    content: prompt,
                } as UserChatMessage,
            };
            set(() => ({prompt, chatHistory: newHistory}));
        },
        initialSummary: false,
        setInitialSummary: (initialSummary) => set(() => ({initialSummary})),

        computed: {
            get files() {
                const state = get();
                const systemMessage = state.chatHistory[state.selectedSystemMessage] as SystemChatMessage;
                return systemMessage?.files;
            },
            get isStreaming() {
                const state = get();
                return state.selectedSystemMessage === state.streamingSystemMessageId;
            },
        },
    };

    return defaultState;
});

export const createUserChatMessage: (options: {prompt?: string; parentId: string}) => UserChatMessage = ({
    prompt,
    parentId,
}) => {
    return {
        id: uuidv4(),
        parentId: parentId,
        role: 'user',
        createDate: new Date().toISOString(),
        content: prompt || '',
        promptType: DEFAULT,
    };
};

export const createSystemChatMessage: (props: {
    view?: AstView;
    appCode: string;
    backgroundColor?: string;
    fontStrings?: string[];
    parentId: string | null;
    previousFiles?: SandpackFiles;
    content?: SystemChatMessage['content'];
}) => SystemChatMessage = ({appCode, backgroundColor, fontStrings, parentId, previousFiles, content, view}) => {
    const inFiles = previousFiles
        ? _.cloneDeep(previousFiles)
        : {
              '/index.tsx': {
                  code: getIndexFile(fontStrings, backgroundColor),
              },
              '/styles.css': {
                  code: getStyleCSS(view),
              },
          };
    return {
        id: uuidv4(),
        role: 'system',
        gptResponse: '',
        createDate: new Date().toISOString(),
        content: content || parseGptResponse(appCode),
        parentId: parentId,
        files: {
            ...inFiles,
            [APP_FILE_NAME]: {
                code: appCode,
            },
        },
    };
};

export function updateSystemChatMessageFromGptResponse(id: string, gptResponse: string, scopedCode: string) {
    const {chatHistory, setChatHistory} = useAiGenStoreBase.getState();

    const message = chatHistory[id];
    if (message.role !== 'system') {
        throw new Error('updateSystemChatMessage called with a non-system message');
    }

    const currentCode = message.files[APP_FILE_NAME].code;

    message.gptResponse = gptResponse;
    message.content = parseGptResponse(gptResponse, currentCode, scopedCode);
    updateFilesFromContent(message, message.content);
    setChatHistory({...chatHistory, [id]: {...message}});
}

function updateFilesFromContent(message: SystemChatMessage, content: Array<SystemChatMessageContent>) {
    content.forEach((entry) => {
        if (entry.type === 'code') {
            message.files[entry.fileName].code = formatSandpackCode(entry.content);
        }
    });
}

export function parseGptResponse(
    gptResponse: string,
    currentCode?: string,
    scopedCode?: string
): Array<SystemChatMessageContent> {
    const codeStartIndex = gptResponse.search(/```.*/);
    const checkboxStartIndex = gptResponse.indexOf('```CHECKBOX');
    const hasCodeStart = codeStartIndex !== -1;
    const hasCheckBoxStart = checkboxStartIndex !== -1;
    const result: Array<SystemChatMessageContent> = [];

    if (!hasCodeStart && !hasCheckBoxStart) {
        return [{type: 'text', content: gptResponse}];
    }

    const itemStart = hasCodeStart ? codeStartIndex : checkboxStartIndex;

    const preText = gptResponse.substring(0, itemStart);

    result.push({
        type: 'text',
        content: preText.replace('CODE PREVIEW', '').trim(),
    });

    const codeText = gptResponse.substring(itemStart, gptResponse.length).replace(/```.*/, '');

    const endIndex = codeText.indexOf('```');
    const hasEnd = endIndex !== -1;

    const trimmedCode = hasEnd ? codeText.substring(0, endIndex).trim() : codeText.replace('```', '').trim();
    const postText = hasEnd ? codeText.substring(endIndex, gptResponse.length).replace('```', '') : '';

    if (hasCodeStart && !hasCheckBoxStart && !hasEnd && codeText === '') {
        return [{type: 'text', content: gptResponse}];
    }

    if (hasCheckBoxStart) {
        result.push({
            type: 'checkbox',
            fileName: APP_FILE_NAME,
            content: trimmedCode,
        });
    } else {
        result.push({
            type: 'code',
            fileName: APP_FILE_NAME,
            content: scopedCode && currentCode ? currentCode.replace(scopedCode, trimmedCode) : trimmedCode,
        });
    }

    if (postText) {
        result.push({
            type: 'text',
            content: postText.trim(),
        });
    }

    return result;
}

export function setLatestToActive(cb?: (userMessage: UserChatMessage, systemMessage: SystemChatMessage) => void): void {
    const chat = useAiGenStoreBase.getState().chatHistory;
    const chain = getActiveChatChain(chat);

    const latestUserMessage = chain[chain.length - 1] as UserChatMessage;
    const latestSystemMessage = chain[chain.length - 2] as SystemChatMessage;

    useAiGenStoreBase.getState().setSelectedSystemMessage(latestSystemMessage.id);
    useAiGenStoreBase.getState().setSelectedUserMessage(latestUserMessage.id);

    if (cb) {
        cb(latestUserMessage, latestSystemMessage);
    }
}

export function summarizeIfNeeded(getModifications: () => Promise<void>, setPrompt: (value: string) => void): void {
    summarize(getModifications, setPrompt);
}

export async function summarize(
    getModifications: () => Promise<void>,
    setPrompt: (value: string) => void
): Promise<void> {
    setPrompt(
        `Please summarize this component and its role in an application from a product (not technical) perspective, and do not change the code. Make your summary a max of 50 words.`
    );
    await getModifications();
    setPrompt('PROPS');
    await getModifications();
    setPrompt(RECOMMENDATION_PROMPT);
    await getModifications();
}

export function setUserMessageIdToActive(messageId: string): void {
    const chat = useAiGenStoreBase.getState().chatHistory;
    if (Object.keys(chat).length === 0) return;

    const foundUserMessage = messageId;

    const userMessage = chat[foundUserMessage];
    if (!userMessage) {
        return;
    }
    const foundSystemMessage = userMessage.parentId!;

    useAiGenStoreBase.getState().setSelectedSystemMessage(foundSystemMessage);
    useAiGenStoreBase.getState().setSelectedUserMessage(foundUserMessage);
}

export function getActiveChatChain(chat: ChatHistory): ChatMessage[] {
    return buildParentMap(chat);
}
/**
 * Gives the history of a message. Works with branching paths.
 * ```
 * [message of messageId, parent, grandparent, ...]
 * ```
 */
export function getHistoryOfMessage(messageId: string): ChatMessage[] {
    const history: ChatMessage[] = [];

    const chat = useAiGenStoreBase.getState().chatHistory;

    let current = chat[messageId];
    if (!current) {
        return [];
    }
    history.push(current);
    while (current.parentId) {
        current = chat[current.parentId];
        history.push(current);
    }

    return history;
}

export function convertSystemMessageToAssistant(message: SystemChatMessage): GPTMessage {
    const contentsWithCode = message.content.map((content) => {
        if (content.type === 'text') {
            return content.content;
        } else if (content.type === 'checkbox') {
            // hack because we're only saving the recommendations, not the full template.
            // wrap in template so gpt will more consistently but its responses in the template
            return '```CHECKBOX\n' + content.content + '```';
        }
        return message.files[APP_FILE_NAME].code;
    });

    return {
        role: 'assistant',
        content: contentsWithCode.join('\n'),
    };
}

export function convertUserMessageToUserGPTMessage(message: UserChatMessage): GPTMessage {
    return {
        role: message.role,
        content: message.content,
    };
}

export function mapChatMessageToGptMessage(message: ChatMessage): GPTMessage {
    if (message.role === 'system') {
        return convertSystemMessageToAssistant(message);
    } else if (message.role === 'user') {
        return convertUserMessageToUserGPTMessage(message);
    } else {
        throw new Error('Invalid role');
    }
}

function buildParentMap(chat: ChatHistory) {
    if (Object.values(chat).length === 0) {
        return [];
    }

    const parentToChildMap: Record<string, ChatMessage[]> = {};

    Object.values(chat).forEach((child) => {
        if (!child.parentId) {
            if (parentToChildMap['root']) {
                throw new Error('Multiple roots');
            }
            parentToChildMap['root'] = [child];
        } else if (parentToChildMap[child.parentId]) {
            parentToChildMap[child.parentId].push(child);
        } else {
            parentToChildMap[child.parentId] = [child];
        }
    });

    const chain: ChatMessage[] = [];

    const root = parentToChildMap['root'][0];

    chain.push(root);

    let current = chain[chain.length - 1];

    while (parentToChildMap[current.id]) {
        const next = current.preferredChildId ? chat[current.preferredChildId] : parentToChildMap[current.id][0];
        chain.push(next);
        current = next;
    }

    return chain;
}

export function getSystemMessageFromUserMessage(userMessageId: string): SystemChatMessage | undefined {
    const chat = useAiGenStoreBase.getState().chatHistory;

    const userMessage = chat[userMessageId];

    if (!userMessage || !userMessage.parentId) {
        return;
    }

    const systemMessage = chat[userMessage.parentId];

    return systemMessage as SystemChatMessage;
}

export function getSiblingsChildIdsFromUserMessage(userMessageId: string): string[] {
    const chatHistory = useAiGenStoreBase.getState().chatHistory;
    const userMessage = chatHistory[userMessageId] as UserChatMessage;
    const parentSystemMessage = userMessage.parentId;

    const siblings = pickBy(chatHistory, (msg) => msg.parentId === parentSystemMessage);
    return values(siblings)
        .sort((a, b) => a.createDate.localeCompare(b.createDate))
        .map((msg) => msg.id);
}

export function createNewMessageSet(prompt?: string) {
    const state = useAiGenStoreBase.getState();
    const sourceSystemChatMessage = state.chatHistory[state.selectedSystemMessage] as SystemChatMessage;
    const sourceUserChatMessage = state.chatHistory[state.selectedUserMessage];

    const systemMessage = createSystemChatMessage({
        appCode: sourceSystemChatMessage.files[APP_FILE_NAME].code,
        parentId: sourceUserChatMessage.id,
        previousFiles: sourceSystemChatMessage.files,
    });

    const userMessage = createUserChatMessage({
        prompt,
        parentId: systemMessage.id,
    });

    const newHistory: ChatHistory = {
        ...state.chatHistory,
        [systemMessage.id]: systemMessage,
        [userMessage.id]: userMessage,
    };

    state.setChatHistory(newHistory);
    return {systemMessage, userMessage};
}

export const useAiGenStore = createSelectors(useAiGenStoreBase);

interface RequestCodeChangesState {
    requestChanges: boolean;
    setRequestChanges: (value: boolean) => void;
}

export const useRequestCodeChangesStore = create<RequestCodeChangesState>((set) => ({
    requestChanges: false,
    setRequestChanges: (value: boolean) => set((state) => ({...state, requestChanges: value})),
}));

export const flagStreamingMessageAsErrored = () => {
    const state = useAiGenStoreBase.getState();
    const {streamingSystemMessageId} = state;
    if (streamingSystemMessageId) {
        const systemMessage = state.chatHistory[streamingSystemMessageId] as SystemChatMessage;
        const sourceUserChatMessage = systemMessage.parentId
            ? (state.chatHistory[systemMessage.parentId] as UserChatMessage)
            : undefined;

        const updates: ChatHistory = {
            [streamingSystemMessageId]: {...systemMessage, didError: true} as SystemChatMessage,
        };
        if (sourceUserChatMessage) {
            updates[sourceUserChatMessage.id] = {...sourceUserChatMessage, didError: true} as UserChatMessage;
        }

        state.setChatHistory({
            ...state.chatHistory,
            ...updates,
        });
    }
};
